import { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { useSendSignal, SignalTypes } from '@/contexts/useSignal';
import { useSessionContext } from '@/contexts/useSession';
import { videoPinnedStreamsState, streamsState } from '@/recoil/atom/streamState';
import { getUserIdFromConnection } from '@/helpers/user';
import { customBugsnagLogger } from '@/utils/bugsnag';
import { computePinDiff } from './computePinDiff';

export { computePinDiff };

const useBroadcastVideoPins = () => {
	const videoPinnedStreams = useRecoilValue(videoPinnedStreamsState);
	const streams = useRecoilValue(streamsState);
	const sendVideoPinStatus = useSendSignal(SignalTypes.VIDEO_PIN_STATUS);
	const { getSession } = useSessionContext();

	const previousPinSetRef = useRef<string[]>([]);
	// Maintained from session events. Note: if OT skips a `connectionDestroyed`
	// (rare reconnect edge case), the entry leaks until next mount — accepted.
	const connectionsRef = useRef<Map<string, OT.Connection>>(new Map());
	const videoPinnedRef = useRef<string[]>(videoPinnedStreams);
	const sendVideoPinStatusRef = useRef(sendVideoPinStatus);

	// Keep refs current so listeners read fresh values without re-subscribing
	useEffect(() => {
		videoPinnedRef.current = videoPinnedStreams;
	}, [videoPinnedStreams]);

	useEffect(() => {
		sendVideoPinStatusRef.current = sendVideoPinStatus;
	}, [sendVideoPinStatus]);

	// Seed connectionsRef from currently-known streams (on mount + when streams update)
	useEffect(() => {
		Object.entries(streams).forEach(([userId, stream]) => {
			if (stream?.connection) {
				connectionsRef.current.set(userId, stream.connection);
			}
		});
	}, [streams]);

	// Track session connections via events
	useEffect(() => {
		const session = getSession();
		if (!session) return undefined;

		const handleConnectionCreated = ({ connection }: { connection: OT.Connection }) => {
			const userId = getUserIdFromConnection(connection);
			connectionsRef.current.set(userId, connection);

			// Bootstrap: if this newly-connected user is in our current pin set, send isPinned=true
			if (videoPinnedRef.current.includes(userId)) {
				customBugsnagLogger.debug(
					`Bootstrapping pin status to ${userId}: true`
				);
				sendVideoPinStatusRef.current({ isPinned: true }, connection);
			}
		};

		const handleStreamCreated = ({ stream }: { stream: OT.Stream }) => {
			if (stream?.connection) {
				const userId = getUserIdFromConnection(stream.connection);
				connectionsRef.current.set(userId, stream.connection);
			}
		};

		const handleConnectionDestroyed = ({ connection }: { connection: OT.Connection }) => {
			const userId = getUserIdFromConnection(connection);
			connectionsRef.current.delete(userId);
		};

		session.on('connectionCreated', handleConnectionCreated);
		session.on('streamCreated', handleStreamCreated);
		session.on('connectionDestroyed', handleConnectionDestroyed);

		return () => {
			session.off('connectionCreated', handleConnectionCreated);
			session.off('streamCreated', handleStreamCreated);
			session.off('connectionDestroyed', handleConnectionDestroyed);
		};
	}, [getSession]);

	// Diff effect — emit directed signals for each added/removed studentId
	useEffect(() => {
		const { added, removed } = computePinDiff(
			previousPinSetRef.current,
			videoPinnedStreams
		);

		added.forEach((studentId) => {
			const conn = connectionsRef.current.get(studentId);
			if (!conn) {
				customBugsnagLogger.debug(
					`Skipping pin broadcast to ${studentId}: connection not yet known`
				);
				return;
			}
			customBugsnagLogger.debug(
				`Broadcasting pin status to student ${studentId}: true`
			);
			sendVideoPinStatusRef.current({ isPinned: true }, conn);
		});

		removed.forEach((studentId) => {
			const conn = connectionsRef.current.get(studentId);
			if (!conn) {
				customBugsnagLogger.debug(
					`Skipping unpin broadcast to ${studentId}: connection not yet known`
				);
				return;
			}
			customBugsnagLogger.debug(
				`Broadcasting pin status to student ${studentId}: false`
			);
			sendVideoPinStatusRef.current({ isPinned: false }, conn);
		});

		previousPinSetRef.current = videoPinnedStreams;
	}, [videoPinnedStreams]);
};

export default useBroadcastVideoPins;
